// Public API. Single entry point for product code.
// Lazy SDK loading, consent-gated, fail-safe.

import { APP_ENV, APP_VERSION, isBrowser, isDebugEnabled } from "./env";
import { consent } from "./consent";
import { buildEnvelope, resetAnonymousId, setUserId } from "./context";
import { bufferPreConsent, checkRateLimit, drainPreConsent, offlineEnqueue, offlineList, offlineDelete } from "./queue";
import { debugLog, debugWarn } from "./debug";
import { metrics } from "./metrics";
import { EventSchema, type EventName, type EventPayload, getAllowlistFor } from "./schema";
import { maskObject } from "./masking";
import { hashUserId } from "./hash";

import { initUtm } from "./utm";
import { touchSession, endSessionExplicit } from "./session";
import { SAMPLING } from "./config";

import type { Provider } from "./providers/types";
import { ConsoleProvider } from "./providers/console";
import { SentryProvider } from "./providers/sentry";
import { VitalsProvider } from "./providers/vitals";
import { GA4Provider } from "./providers/ga4";
import { PostHogProvider } from "./providers/posthog";
import type { IdentifyTraits, PageInfo, EnrichedEvent } from "./types";

// ---- Provider registry ----
const consoleProvider = new ConsoleProvider();
const sentryProvider = new SentryProvider();
const vitalsProvider = new VitalsProvider();
const ga4Provider = new GA4Provider();
const posthogProvider = new PostHogProvider();

const allProviders: Provider[] = [];

function isLoadable(p: Provider): boolean {
  // All required consent categories must be allowed (essential is always allowed).
  return p.categories.every((c) => c === "essential" || consent.allows(c));
}

async function loadProvider(p: Provider): Promise<void> {
  if (p.isLoaded()) return;
  try {
    await p.load();
  } catch (err) {
    metrics.providerFailure(p.name);
    debugWarn(`provider ${p.name} load failed`, err);
  }
}

async function loadAllAuthorized(): Promise<void> {
  await Promise.all(allProviders.filter(isLoadable).map(loadProvider));
}

// ---- Internal dispatch ----

function dispatch(evt: EnrichedEvent): void {
  metrics.inc("events_dispatched_total");
  for (const p of allProviders) {
    // Authorization check
    if (!isLoadable(p)) continue;
    // essentialSinksOnly → restrict to providers with essential-only categories.
    if (evt.essentialSinksOnly && !p.categories.every((c) => c === "essential")) continue;
    if (!p.isLoaded()) continue;
    try {
      void p.track(evt);
    } catch (err) {
      metrics.providerFailure(p.name);
      debugWarn(`provider ${p.name} track threw`, err);
    }
  }
}

function dispatchPage(page: PageInfo, evt: EnrichedEvent): void {
  for (const p of allProviders) {
    if (!isLoadable(p) || !p.isLoaded()) continue;
    try {
      p.page?.(page, evt);
    } catch (err) {
      metrics.providerFailure(p.name);
      debugWarn(`provider ${p.name} page threw`, err);
    }
  }
}

// ---- Schema validation (dev only) ----

function validate<E extends EventName>(name: E, payload: EventPayload<E>): boolean {
  const desc = EventSchema[name];
  if (!desc) return false;
  const result = desc.payload.safeParse(payload);
  if (result.success) return true;

  metrics.inc("dropped_schema_invalid");
  if (APP_ENV === "local") {
    // eslint-disable-next-line no-console
    console.error(`[analytics] schema invalid for "${name}":`, result.error.format(), payload);
  }
  // 1% sample to Sentry in preview/prod (only if loaded).
  if (APP_ENV !== "local" && Math.random() < SAMPLING.schema_invalid_sample_rate) {
    sentryProvider.captureException(new Error(`analytics_schema_invalid:${String(name)}`), {
      payload_keys: Object.keys(payload || {}),
      issues: result.error.issues.map((i) => `${i.path.join(".")}: ${i.code}`),
    });
  }
  return false;
}

// ---- Public API ----

export const analytics = {
  /**
   * Track a typed event. Schema is validated only in local/dev; in preview/prod
   * a 1% sample of invalid events is reported to Sentry.
   */
  track<E extends EventName>(name: E, payload: EventPayload<E>): void {
    if (!isBrowser()) return;
    if (APP_ENV === "killed") {
      metrics.inc("dropped_kill_switch");
      metrics.inc("dropped_events_total");
      return;
    }
    if (!checkRateLimit()) return;

    // Dev-only schema validation
    if (APP_ENV === "local" && !validate(name, payload)) {
      return; // drop in dev to enforce schema
    }

    const desc = EventSchema[name];
    const allow = getAllowlistFor(name);
    const maskedPayload = maskObject(payload as Record<string, unknown>, allow);
    // Sampling (drop low-priority in prod if rate < 1)
    if (Math.random() > SAMPLING.event_sample_rate) return;

    const envelope = buildEnvelope(name, maskedPayload, {
      consentReplayable: desc.consentReplayable,
      essentialSinksOnly: desc.essentialSinksOnly,
      priority: desc.priority,
    });

    if (!consent.isDecided()) {
      bufferPreConsent(envelope);
      debugLog(`track:buffered`, envelope);
      return;
    }

    debugLog(`track:dispatch`, envelope);
    dispatch(envelope);
  },

  page(page: PageInfo): void {
    if (!isBrowser() || APP_ENV === "killed") return;
    // Touch session; emit session_started/ended automatically.
    const transition = touchSession();
    if (transition.ended) {
      this.track("session_ended", {
        reason: transition.ended.reason,
        duration_ms: transition.ended.duration_ms,
      });
    }
    // First touch in this fresh session → emit session_started
    if (transition.current.started_at === transition.current.last_activity) {
      const utm = (() => {
        try {
          return JSON.parse(sessionStorage.getItem("nr_utm_session_v1") || "null") ?? null;
        } catch {
          return null;
        }
      })();
      this.track("session_started", {
        referrer_host: (() => {
          try {
            return document.referrer ? new URL(document.referrer).hostname : null;
          } catch {
            return null;
          }
        })(),
        utm_source: utm?.source ?? null,
        utm_medium: utm?.medium ?? null,
        utm_campaign: utm?.campaign ?? null,
      });
    }

    this.track("page_viewed", {
      current_path: page.current_path,
      previous_path: page.previous_path,
      title: page.title,
    });

    // Bridge to per-provider page hook
    const envelope = buildEnvelope("page_viewed", page as unknown as Record<string, unknown>, {
      consentReplayable: true,
      essentialSinksOnly: false,
      priority: "low",
    });
    dispatchPage(page, envelope);
  },

  identify(userId: string, traits?: IdentifyTraits): void {
    if (!isBrowser()) return;
    // F1 hardening: hash raw auth UUID at the boundary so NO downstream
    // provider (console, Sentry, future PostHog) ever receives the original
    // identifier. Truncated FNV fold — irreversible-enough, deterministic.
    const safeId = hashUserId(userId) ?? "anon";
    setUserId(safeId);
    for (const p of allProviders) {
      if (!isLoadable(p) || !p.isLoaded()) continue;
      try {
        p.identify?.(safeId, traits);
      } catch (err) {
        metrics.providerFailure(p.name);
        debugWarn(`provider ${p.name} identify threw`, err);
      }
    }
  },


  alias(from: string, to: string): void {
    for (const p of allProviders) {
      if (!isLoadable(p) || !p.isLoaded()) continue;
      try {
        p.alias?.(from, to);
      } catch (err) {
        metrics.providerFailure(p.name);
      }
    }
  },

  reset(opts?: { eraseDevice?: boolean }): void {
    setUserId(null);
    for (const p of allProviders) {
      try {
        p.reset?.();
      } catch {
        /* noop */
      }
    }
    endSessionExplicit("explicit");
    if (opts?.eraseDevice) {
      resetAnonymousId();
    }
  },

  // Bridge for ErrorBoundary
  captureException(err: unknown, ctx?: Record<string, unknown>): void {
    sentryProvider.captureException(err, ctx);
    // Sprint 3: capturing an error makes this session "interesting" — flip
    // the replay override so the next allow-listed pageview gets recorded
    // (subject to consent). Deny-list still wins.
    try {
      import("./replay").then((m) => m.markErrorOverride()).catch(() => undefined);
    } catch {
      /* noop */
    }
  },

  /**
   * Sprint 3: route-change hook. Called from useTrackPageView after each
   * navigation. Applies the replay policy AND resets UX instrumentation
   * scoped to the page (scroll depth, abandoned-form flush).
   */
  onRouteChange(path: string): void {
    try {
      posthogProvider.applyReplayPolicy(path);
    } catch {
      /* noop */
    }
  },
};

// ---- Bootstrap ----

let bootstrapPromise: Promise<void> | null = null;

/**
 * Essential bootstrap. Runs as early as possible (main.tsx), before render.
 * Loads consent state + Sentry (essential) + UTM capture.
 * Heavy SDKs and analytics-tier providers are deferred until consent + idle.
 */
export function bootstrapEssential(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    if (!isBrowser()) return;

    // Hook web-vitals into our pipeline.
    vitalsProvider.setEmitter((name, payload) => {
      analytics.track(name as EventName, payload as never);
    });

    allProviders.push(consoleProvider, sentryProvider, vitalsProvider, ga4Provider, posthogProvider);

    consent.init();
    initUtm();

    // Console provider in non-prod only.
    if (APP_ENV !== "production") {
      await loadProvider(consoleProvider);
    }

    // Sentry is essential — always allowed.
    await loadProvider(sentryProvider);

    // Vitals load deferred to idle to avoid LCP impact.
    if (typeof window !== "undefined") {
      const start = () => void loadProvider(vitalsProvider);
      if ("requestIdleCallback" in window) {
        (window as unknown as { requestIdleCallback: (fn: () => void, o?: { timeout: number }) => void })
          .requestIdleCallback(start, { timeout: 4000 });
      } else {
        setTimeout(start, 2000);
      }
    }

    // Drain pre-consent on decision.
    consent.subscribe(async (s) => {
      if (s.status === "unknown") return;
      // Reload providers in case categories changed.
      await loadAllAuthorized();
      // Drain buffered events (only consentReplayable=true survive).
      const events = drainPreConsent();
      for (const evt of events) dispatch(evt);

      // Track the consent decision itself.
      const kind = consent.decisionKind();
      if (kind) {
        analytics.track("consent_decision_made", {
          decision: kind,
          categories: s.categories,
          policy_version: s.version,
        });
      }
      // Sprint 3: replay must stop the instant consent is withdrawn.
      try {
        if (typeof window !== "undefined") {
          posthogProvider.applyReplayPolicy(window.location.pathname);
        }
      } catch {
        /* noop */
      }
    });

    // Online → drain offline queue.
    if (typeof window !== "undefined") {
      const drainOffline = async () => {
        const recs = await offlineList();
        for (const r of recs) {
          dispatch(r.e);
          await offlineDelete(r.k);
        }
      };
      window.addEventListener("online", () => void drainOffline());
      window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void drainOffline();
      });
    }

    if (isDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.info(`[analytics] bootstrapped env=${APP_ENV} version=${APP_VERSION}`);
    }
  })();
  return bootstrapPromise;
}

// Expose internal hooks (for tests/debug)
export const __internal = { allProviders, dispatch, offlineEnqueue };
export { consent } from "./consent";
export { metrics } from "./metrics";
export type { EventName, EventPayload };

// Sprint 3 surfaces.
export { flags } from "./flags";
export { evaluateReplay, markErrorOverride, REPLAY } from "./replay";

